/**
 * APP-AI-2: state for the drawer's "AI suggestions" group.
 *
 * Every suggestion is INACTIVE until the user accepts it (spec §2.2): nothing
 * is pre-selected and there is no "accept all" — only "Accept selected" over
 * rows the user ticked. One request in flight at a time. A text edit makes the
 * suggestions stale, so they are cleared.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getPasteAssistStatus,
  requestPasteAssist,
  type PasteAssistResponse,
  type PasteSuggestion,
} from '@/lib/pasteAssistApi';

export function usePasteAssist(open: boolean) {
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PasteAssistResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Ticked for "Accept selected" — never pre-filled. */
  const [selected, setSelected] = useState<string[]>([]);
  const [accepted, setAccepted] = useState<string[]>([]);
  /** The session the accepted ids belong to; outlives the suggestion list. */
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string[]>([]);
  /** Drops the answer of a request made against text that has since changed. */
  const generation = useRef(0);
  /**
   * One provider request at a time (spec §8). Separate from `generation`: a
   * text edit discards the answer but the request is still running, so the
   * button stays busy until it settles.
   */
  const inFlight = useRef(false);
  /** Accepted date-range ids: an edit drops the AI dates, so it drops these too. */
  const acceptedDates = useRef<string[]>([]);

  /** Full reset: drawer (re)opened or the batch cleared. */
  const clear = useCallback(() => {
    generation.current += 1;
    setResponse(null);
    setError(null);
    setSelected([]);
    setAccepted([]);
    setDismissed([]);
    setSessionId(null);
    acceptedDates.current = [];
  }, []);

  /**
   * The text changed: the suggestion LIST is stale and goes, but what was
   * already accepted stays accepted (its rows stay staged), so its provenance
   * — session id + accepted ids — is kept for the commit.
   */
  const invalidate = useCallback(() => {
    generation.current += 1;
    setResponse(null);
    setError(null);
    setSelected([]);
    setDismissed([]);
    const dropped = acceptedDates.current;
    setAccepted((prev) => prev.filter((id) => !dropped.includes(id)));
    acceptedDates.current = [];
  }, []);

  useEffect(() => {
    if (!open) return;
    clear();
    let live = true;
    getPasteAssistStatus()
      .then((r) => { if (live) setAvailable(r.available === true); })
      .catch(() => { if (live) setAvailable(false); });
    return () => { live = false; };
  }, [open, clear]);

  const request = useCallback(async (text: string, needDates: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    setResponse(null);
    setSelected([]);
    setDismissed([]);
    try {
      const r = await requestPasteAssist(text, needDates);
      if (generation.current === mine) {
        setResponse(r);
        // One session per commit: a fresh session replaces the old one (and
        // its acceptances) only when it actually produced one.
        if (r.assist_session_id) {
          setSessionId(r.assist_session_id);
          setAccepted([]);
          acceptedDates.current = [];
        }
      }
    } catch (err) {
      if (generation.current === mine) setError(err instanceof Error ? err.message : 'AI suggestions failed');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  const visible: PasteSuggestion[] = (response?.suggestions ?? []).filter(
    (s) => !accepted.includes(s.suggestion_id) && !dismissed.includes(s.suggestion_id),
  );

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const markAccepted = useCallback((ids: string[], kind: 'unit' | 'date_range' = 'unit') => {
    if (kind === 'date_range') acceptedDates.current = [...acceptedDates.current, ...ids];
    setAccepted((prev) => [...new Set([...prev, ...ids])]);
    setSelected((prev) => prev.filter((x) => !ids.includes(x)));
  }, []);

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => [...prev, id]);
    setSelected((prev) => prev.filter((x) => x !== id));
  }, []);

  return {
    available,
    loading,
    response,
    error,
    visible,
    selected,
    accepted,
    request,
    clear,
    invalidate,
    toggleSelected,
    markAccepted,
    dismiss,
    /** What bulk-assign needs to audit provenance; null when nothing was accepted. */
    provenance: sessionId && accepted.length
      ? { assist_session_id: sessionId, accepted_suggestion_ids: accepted }
      : null,
  };
}
